import React, { useMemo, useRef, useState, useEffect } from "react";
import { X, AlertTriangle, Move } from "lucide-react";

// OptionStrat-style strike axis. Red/green leg pills float above a long
// thin axis with ~9 numeric tick labels. Each pill drops a dashed line to
// its strike. ITM legs show a subtle warning triangle when the strike has
// crossed the spot.
//
// Pill text format: `<strike><P|C> <m>/<d>`  e.g. "755C 6/30"
//
// Props:
//   legs:             array of {action,'BUY'|'SELL', type:'call'|'put', strike, dte, qty}
//   spot:             current underlying price (rendered as a small blue dot)
//   onRemoveLeg:      (idx) => void
//   onLegStrikeChange:(idx, newStrike) => void  // drag-handler
//   listedStrikes:    optional sorted array of tradable strikes — drag snaps to nearest

const monthDay = (dteDays) => {
  const d = new Date();
  d.setDate(d.getDate() + (Number(dteDays) || 0));
  return `${d.getMonth() + 1}/${d.getDate()}`;
};

const niceTicks = (lo, hi, target = 9) => {
  const span = Math.max(hi - lo, 1);
  const rough = span / (target - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const candidates = [1, 2, 2.5, 5, 10].map((c) => c * mag);
  const step = candidates.find((c) => span / c <= target) || candidates[candidates.length - 1];
  const first = Math.ceil(lo / step) * step;
  const ticks = [];
  for (let v = first; v <= hi + step * 0.001; v += step) {
    ticks.push(Math.round(v * 1e6) / 1e6);
    if (ticks.length > target + 3) break;
  }
  return ticks;
};

// Snap a continuous strike value to the nearest listed strike; falls back
// to rounding to the closest 0.5 when no listed strikes are provided.
const snapStrike = (raw, listed) => {
  if (Array.isArray(listed) && listed.length) {
    return listed.reduce(
      (best, s) => (Math.abs(s - raw) < Math.abs(best - raw) ? s : best),
      listed[0],
    );
  }
  return Math.round(raw * 2) / 2;
};

const StrikeRibbon = ({
  legs = [],
  spot,
  onRemoveLeg,
  onLegStrikeChange,
  listedStrikes = null,
  bare = false,
}) => {
  const opts = useMemo(() => legs.filter((l) => l.type !== "stock"), [legs]);

  // Refs declared up front (rules-of-hooks: keep call order stable across renders).
  const axisRef = useRef(null);
  const dragRef = useRef(null);   // { idx, startX, startStrike, rect, liveStrike }
  const [drag, setDrag] = useState(null);

  // Compute axis bounds the same way for both static + drag-preview paths.
  // When dragging, expand the axis to include the live strike so the pill
  // never escapes the box.
  const liveStrikes = opts.map((l, i) => {
    if (drag && drag.idx === i) return drag.liveStrike;
    return +l.strike;
  }).filter((s) => Number.isFinite(s));

  const min = Math.min(...liveStrikes, ...(Number.isFinite(spot) ? [spot] : []));
  const max = Math.max(...liveStrikes, ...(Number.isFinite(spot) ? [spot] : []));
  const range = Math.max(max - min, 1);
  const pad = range * 0.18;
  const lo = min - pad;
  const hi = max + pad;
  const span = hi - lo;
  const xFor = (v) => `${((v - lo) / span) * 100}%`;
  const strikeForX = (clientX, rect) => {
    const f = (clientX - rect.left) / rect.width;
    return lo + Math.max(0, Math.min(1, f)) * span;
  };
  const ticks = useMemo(() => niceTicks(lo, hi), [lo, hi]);

  // Global pointer handlers (attached for the lifetime of a drag only)
  useEffect(() => {
    if (!drag) return undefined;
    const move = (e) => {
      const { rect } = dragRef.current;
      if (!rect) return;
      const raw = strikeForX(e.clientX, rect);
      dragRef.current.liveStrike = raw;
      setDrag({ ...dragRef.current });
    };
    const up = () => {
      const { idx, liveStrike } = dragRef.current || {};
      dragRef.current = null;
      setDrag(null);
      if (idx != null && Number.isFinite(liveStrike) && onLegStrikeChange) {
        const snapped = snapStrike(liveStrike, listedStrikes);
        const original = +opts[idx]?.strike;
        if (Number.isFinite(snapped) && Math.abs(snapped - original) > 0.001) {
          onLegStrikeChange(idx, snapped);
        }
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag != null, onLegStrikeChange, opts, listedStrikes, lo, hi, span]);

  const onPillPointerDown = (e, idx, rect) => {
    if (!onLegStrikeChange) return;
    // Skip drag init when the pointer hits the remove button so the X click
    // path still works unchanged.
    if (e.target.closest('[data-testid^="forge-leg-pill-remove-"]')) return;
    e.preventDefault();
    const raw = strikeForX(e.clientX, rect);
    dragRef.current = {
      idx,
      rect,
      startX: e.clientX,
      startStrike: +opts[idx].strike,
      liveStrike: raw,
    };
    setDrag({ ...dragRef.current });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  if (!opts.length) {
    return (
      <div
        className={`${bare ? "" : "bg-[#0E1A30] border border-white/10 rounded-xl"} px-5 py-4 text-[12px] text-white/45 text-center`}
        data-testid="forge-strike-ribbon-empty"
      >
        Click any <span className="text-[#22C55E] font-medium">Ask</span> or{" "}
        <span className="text-[#EF4444] font-medium">Bid</span> in the chain below to add a leg.
      </div>
    );
  }

  return (
    <div
      className={`${bare ? "" : "bg-[#0E1A30] border border-white/10 rounded-xl"} px-5 pt-2 pb-3 relative`}
      data-testid="forge-strike-ribbon"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[9.5px] uppercase tracking-[0.22em] text-white/40 inline-flex items-center gap-1.5">
          Strikes
          {onLegStrikeChange && (
            <span className="text-white/30 normal-case tracking-normal inline-flex items-center gap-1">
              <Move className="w-2.5 h-2.5" /> drag any pill to retune
            </span>
          )}
        </span>
        <span className="text-[9.5px] uppercase tracking-[0.18em] text-white/35 num">
          {opts.length} leg{opts.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Leg pills above the axis */}
      <div ref={axisRef} className="relative h-12 mb-1">
        {opts.map((l, i) => {
          const isDragging = drag && drag.idx === i;
          // While dragging, render the live (un-snapped) strike for smooth
          // tracking; the snap kicks in on pointer-up.
          const strike = isDragging ? drag.liveStrike : +l.strike;
          if (!Number.isFinite(strike)) return null;
          const isLong = l.action === "BUY";
          const color = isLong ? "#22C55E" : "#EF4444";
          const kind = (l.type || "").toUpperCase()[0];
          const previewSnap = isDragging
            ? snapStrike(strike, listedStrikes)
            : strike;
          const label = `${previewSnap.toFixed(previewSnap % 1 === 0 ? 0 : 1)}${kind} ${monthDay(l.dte)}`;
          const itmRisk = Number.isFinite(spot) && (
            (l.type === "call" && l.action === "SELL" && previewSnap < spot)
            || (l.type === "put" && l.action === "SELL" && previewSnap > spot)
          );
          return (
            <div
              key={`leg-${i}`}
              className="group absolute -translate-x-1/2 flex flex-col items-center"
              style={{
                left: xFor(strike),
                transition: isDragging ? "none" : "left 120ms ease-out",
              }}
              data-testid={`forge-leg-pill-${i}`}
            >
              <div
                onPointerDown={(e) => {
                  const rect = axisRef.current?.getBoundingClientRect();
                  if (rect) onPillPointerDown(e, i, rect);
                }}
                data-testid={`forge-leg-pill-handle-${i}`}
                className={`relative flex items-center gap-1 text-[10.5px] num font-medium px-2 py-1 rounded border whitespace-nowrap select-none ${
                  onLegStrikeChange ? "cursor-ew-resize" : ""
                } ${isDragging ? "scale-110 z-10" : ""}`}
                style={{
                  color,
                  borderColor: `${color}${isDragging ? "FF" : "88"}`,
                  background: `${color}${isDragging ? "33" : "1A"}`,
                  boxShadow: isDragging
                    ? `0 0 0 1px ${color}66 inset, 0 0 24px ${color}66`
                    : `0 0 12px ${color}22`,
                  transition: isDragging ? "none" : "transform 120ms ease-out, box-shadow 120ms",
                  touchAction: "none",
                }}
              >
                {itmRisk && <AlertTriangle className="w-2.5 h-2.5 text-[#FACC15]" />}
                {label}
                {onRemoveLeg && (
                  <button
                    type="button"
                    onClick={() => onRemoveLeg(i)}
                    onPointerDown={(e) => e.stopPropagation()}
                    data-testid={`forge-leg-pill-remove-${i}`}
                    className="opacity-0 group-hover:opacity-100 transition ml-0.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full"
                    style={{ background: `${color}55` }}
                    title="Remove leg"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                )}
              </div>
              <div
                className="flex-1 w-px"
                style={{
                  backgroundImage: `linear-gradient(to bottom, ${color}AA 50%, transparent 50%)`,
                  backgroundSize: "1px 4px",
                  minHeight: 14,
                }}
                aria-hidden="true"
              />
            </div>
          );
        })}
      </div>

      {/* Axis */}
      <div className="relative h-px bg-white/20">
        {ticks.map((t) => (
          <div
            key={`tick-${t}`}
            className="absolute top-0 -translate-x-1/2 w-px h-2 bg-white/35"
            style={{ left: xFor(t) }}
          />
        ))}
        {Array.from({ length: 25 }, (_, i) => i).map((i) => (
          <div
            key={`mid-${i}`}
            className="absolute top-0 -translate-x-1/2 w-px h-1 bg-white/12"
            style={{ left: `${(i / 24) * 100}%` }}
          />
        ))}
        {Number.isFinite(spot) && (
          <div
            className="absolute -translate-x-1/2 -top-1 w-2 h-2 rounded-full bg-[#60A5FA] border border-white/70 shadow"
            style={{ left: xFor(spot) }}
            title={`Spot ${spot.toFixed(2)}`}
            data-testid="forge-spot-marker"
          />
        )}
      </div>

      <div className="relative h-4 mt-1">
        {ticks.map((t) => (
          <div
            key={`lab-${t}`}
            className="absolute -translate-x-1/2 text-[10px] num text-white/55 tabular-nums whitespace-nowrap"
            style={{ left: xFor(t) }}
          >
            {t.toFixed(0)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default StrikeRibbon;
